import { LitElement, html, css } from 'lit';
import { sharedStyles, stateOf } from './base.js';
import { fmt } from '../fmt.js';

// <gt-data-table> — the standard sortable-looking data grid, driven by data.
//
//   const t = document.querySelector('gt-data-table');
//   t.columns = [
//     { key: 'ticker', label: 'Ticker', cls: 'mono' },
//     { key: 'price',  label: 'Price', align: 'right', format: 'price' },
//     { key: 'chg',    label: 'Chg%',  align: 'right', format: 'pctPoints', color: true },
//   ];
//   t.rowKey = 'ticker';
//   t.rows = [...];
//
// SSE contract — patch a single row in place without re-rendering the table:
//   t.updateRow('AAPL', { price: 231.4, chg: 1.2 });
//
// Sorting — set `sortable` and (optionally) an initial sortKey/sortDir; clicking
// a header re-sorts client-side (toggling direction on the active column). Nulls
// and non-finite numbers always sort last. A column is treated as numeric when
// it sets `sortType: 'num'`, is right-aligned, or uses a numeric GT.fmt format;
// otherwise it sorts as text. Override per column with `sortType: 'num'|'text'`.
//
// Row links — set `rowHref` to a fn(row)->url and the whole row becomes a click
// target (use `@click` + stopPropagation inside a column `render` for in-row
// buttons so they don't trigger the row navigation).
//
// column fields: key, label, align (left|right|center), format (a GT.fmt key),
// color (bool — color the cell up/down by sign), cls (extra class), href
// (fn(row)->url makes the cell a link), render (fn(value,row)->string|node),
// sortType ('num'|'text').
export class GtDataTable extends LitElement {
  static properties = {
    columns: { attribute: false },
    rows: { attribute: false },
    rowKey: { type: String },
    dense: { type: Boolean, reflect: true },
    sortable: { type: Boolean, reflect: true },
    sortKey: { type: String },
    sortDir: { type: Number },
    rowHref: { attribute: false },
  };
  constructor() {
    super();
    this.columns = [];
    this.rows = [];
    this.rowKey = 'id';
    this.sortable = false;
    this.sortKey = '';
    this.sortDir = -1;
    this.rowHref = null;
  }

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: var(--fs-sm, 0.82rem);
      }
      thead th {
        position: sticky;
        top: 0;
        text-align: left;
        font-size: var(--fs-label, 0.68rem);
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--muted, #94a3b8);
        padding: var(--space-2, 8px) var(--space-3, 12px);
        border-bottom: 1px solid var(--border, #1e2535);
        background: var(--bg-card, #141826);
        white-space: nowrap;
      }
      tbody td {
        padding: var(--space-3, 12px);
        border-bottom: 1px solid var(--border-soft, #141826);
        color: var(--text, #e2e8f0);
        white-space: nowrap;
      }
      :host([sortable]) thead th {
        cursor: pointer;
        user-select: none;
      }
      :host([sortable]) thead th:hover {
        color: var(--text, #e2e8f0);
      }
      :host([sortable]) thead th.nosort {
        cursor: default;
      }
      thead th.sorted {
        color: var(--accent, #c8a84b);
      }
      /* Every sortable header carries a faint ↕ so sortability is
         discoverable without hovering; the active column swaps it for a
         solid accent ↑/↓. */
      th .sort-arrow {
        margin-left: 3px;
        color: var(--muted-dim, #64748b);
        opacity: 0.55;
        font-size: 0.9em;
      }
      :host([sortable]) thead th:hover .sort-arrow {
        opacity: 1;
        color: var(--text, #e2e8f0);
      }
      th .sort-arrow.active {
        color: var(--accent, #c8a84b);
        opacity: 1;
      }
      :host([dense]) tbody td,
      :host([dense]) thead th {
        padding: var(--space-1, 4px) var(--space-2, 8px);
      }
      tbody tr.clickable {
        cursor: pointer;
      }
      tbody tr:hover td {
        background: color-mix(in srgb, var(--bg-chip, #1a2136) 55%, transparent);
      }
      td.right,
      th.right {
        text-align: right;
      }
      td.center,
      th.center {
        text-align: center;
      }
      td.mono {
        font-family: var(--font-mono, monospace);
        font-variant-numeric: tabular-nums;
      }
      td.up {
        color: var(--up, #22c55e);
      }
      td.down {
        color: var(--down, #ef4444);
      }
      a {
        color: inherit;
        text-decoration: none;
      }
      a:hover {
        color: var(--accent, #c8a84b);
      }
      .empty {
        padding: var(--space-6, 24px);
        text-align: center;
        color: var(--muted, #94a3b8);
        font-size: var(--fs-sm, 0.82rem);
      }
    `,
  ];

  // Patch one row by its rowKey value and re-render (SSE update-by-key).
  updateRow(keyVal, patch) {
    const i = (this.rows || []).findIndex((r) => r[this.rowKey] === keyVal);
    if (i < 0) return false;
    this.rows = this.rows.map((r, j) => (j === i ? { ...r, ...patch } : r));
    return true;
  }

  static _NUMERIC_FORMATS = new Set([
    'price', 'money', 'num', 'int', 'compact', 'aum', 'pct', 'pctPoints', 'pctPlain',
  ]);

  _numericCol(col) {
    if (!col) return true;
    if (col.sortType) return col.sortType === 'num';
    return col.align === 'right' || GtDataTable._NUMERIC_FORMATS.has(col.format);
  }

  // Rows sorted by the active column (nulls / non-finite last). Non-mutating —
  // the unsorted `this.rows` stays the source of truth for updateRow().
  _sortedRows() {
    const rows = this.rows || [];
    if (!this.sortable || !this.sortKey) return rows;
    const col = (this.columns || []).find((c) => c.key === this.sortKey);
    const numeric = this._numericCol(col);
    const dir = this.sortDir || -1;
    const isNull = (v) => v == null || (typeof v === 'number' && !isFinite(v));
    return [...rows].sort((a, b) => {
      const av = a[this.sortKey];
      const bv = b[this.sortKey];
      if (isNull(av) && isNull(bv)) return 0;
      if (isNull(av)) return 1;
      if (isNull(bv)) return -1;
      if (numeric) return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }

  _onSort(col) {
    if (this.sortKey === col.key) {
      this.sortDir = -(this.sortDir || -1);
    } else {
      this.sortKey = col.key;
      this.sortDir = this._numericCol(col) ? -1 : 1;
    }
  }

  _cell(col, row) {
    const raw = row[col.key];
    let inner;
    if (col.render) {
      // render returns cell CONTENT (string | node | TemplateResult); the
      // component owns the <td> wrapper so alignment/classes stay consistent.
      inner = col.render(raw, row);
    } else {
      const text =
        col.format && fmt[col.format]
          ? fmt[col.format](raw)
          : raw == null
            ? fmt.DASH
            : String(raw);
      inner = col.href ? html`<a href=${col.href(row)}>${text}</a>` : text;
    }
    const classes = [
      col.align || '',
      col.cls || '',
      col.color ? stateOf(raw) : '',
    ]
      .filter(Boolean)
      .join(' ');
    return html`<td class=${classes}>${inner}</td>`;
  }

  render() {
    const cols = this.columns || [];
    const rows = this._sortedRows();
    if (!rows.length) {
      return html`<div class="empty"><slot name="empty">No data</slot></div>`;
    }
    return html`
      <table>
        <thead>
          <tr>
            ${cols.map((c) => {
              const canSort = this.sortable && c.sortable !== false;
              const sorted = canSort && this.sortKey === c.key;
              const arrow = sorted
                ? html`<span class="sort-arrow active">${this.sortDir === 1 ? '↑' : '↓'}</span>`
                : canSort && c.label
                  ? html`<span class="sort-arrow">↕</span>`
                  : '';
              const cls = [c.align || '', sorted ? 'sorted' : '', canSort ? '' : 'nosort']
                .filter(Boolean)
                .join(' ');
              return html`<th
                class=${cls}
                @click=${canSort ? () => this._onSort(c) : null}
              >
                ${c.label ?? ''}${arrow}
              </th>`;
            })}
          </tr>
        </thead>
        <tbody>
          ${rows.map((row) => {
            const href = this.rowHref ? this.rowHref(row) : null;
            return html`<tr
              class=${href ? 'clickable' : ''}
              @click=${href ? () => {
                window.location.href = href;
              } : null}
            >
              ${cols.map((c) => this._cell(c, row))}
            </tr>`;
          })}
        </tbody>
      </table>
    `;
  }
}
customElements.define('gt-data-table', GtDataTable);
